import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { $getRoot,$isParagraphNode  } from "lexical";

dayjs.extend(utc);
dayjs.extend(timezone);

const currentTimeZone = dayjs.tz.guess();

export const replaceMergeFields = (
  templateHtml: string,
  data: Record<string, string>,
  templateMetaData?: { key: string; value: string }[]
): string => {
  return replaceWithVisited(templateHtml, data, templateMetaData, new Set());
};

// Internal helper that carries the recursion guard set through nested resolutions
function replaceWithVisited(
  templateHtml: string,
  data: Record<string, string>,
  templateMetaData: { key: string; value: string }[] | undefined,
  visited: Set<string>
): string {
  if (!templateHtml) return "";

  const resolveField = (key: string): string => {
    const normalizedKey = key.startsWith("mm.") ? key.slice(3) : key;

    // Aliases mapped to canonical fields in data
    const aliasMap: Record<string, string> = {
      Mail: "Email",
      MailingAddress: "Email",
      Email: "Email",
    };

    switch (normalizedKey) {
      case "FullName": {
        const first = data?.["FirstName"] || "";
        const last = data?.["LastName"] || "";
        return (first + " " + last).trim();
      }
      case "Address": {
        const parts = [
          data?.["StreetLine1"],
          data?.["City"],
          data?.["State"],
          data?.["PostalCode"],
          data?.["Country"],
        ].filter(Boolean);
        return parts.join(", ");
      }
      case "Date":
      case "CurrentDate":
        return dayjs().tz(currentTimeZone).format("MMM DD, YYYY");
      case "Day":
        return dayjs().tz(currentTimeZone).format("dddd");
      case "Month":
        return dayjs().tz(currentTimeZone).format("MMMM");
      case "Year":
        return dayjs().tz(currentTimeZone).format("YYYY");
      case "MonthName":
        return dayjs().tz(currentTimeZone).format("MMMM");
      case "CityStateZip":
        return [data?.["City"], data?.["State"], data?.["PostalCode"]]
          .filter(Boolean)
          .join(", ");
      case "Birthday":
        return data?.["Birthday"]
          ? dayjs(data["Birthday"]).tz(currentTimeZone).format("MMM DD, YYYY")
          : "";
      case "StreetAddress":
        return data?.["StreetLine1"] || "";
    }

    const canonicalKey = aliasMap[normalizedKey] || normalizedKey;

    // If this key is already being resolved in the current chain, stop (cycle).
    if (visited.has(canonicalKey)) {
      return `%%${key}%%`;
    }

    // 1) Try main data first (no recursion tracking needed for plain data lookups)
    if (data?.[canonicalKey]) {
      return data[canonicalKey];
    }

    // 2) Try from metadata (may contain nested placeholders)
    const metaItem = templateMetaData?.find((item) => item.key === canonicalKey);
    if (metaItem?.value) {
      // Mark this key as being resolved in the current chain
      visited.add(canonicalKey);
      const resolved = replaceWithVisited(metaItem.value, data, templateMetaData, visited);
      // Done resolving this key in this chain; allow other branches to use it
      visited.delete(canonicalKey);
      return resolved;
    }

    // 3) Fallback if not found
    return `%%${key}%%`;
  };

  return templateHtml.replace(/%%(.*?)%%/g, (match, keyRaw) => {
    if (!keyRaw) return match;
    return resolveField(keyRaw.trim()) || match;
  });
}


export const toPascalCase = (str: string): string =>
    str.replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase());

  // Custom key mappings
  const customKeyMap: Record<string, string> = {
    primary_email_address: "Email",
    primary_phone_number: "Phone",
    website_url: "Website",
    dob: "Birthday"
  };

  // Keys inside an "address" object and their new names
  const addressKeyMap: Record<string, string> = {
    street_line_1: "StreetAddress",
    city: "City",
    country: "Country",
    zipcode: "PostalCode",
    state: "State",
  };

export  const convertKeysToPascalCase = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(convertKeysToPascalCase);
    } else if (obj !== null && typeof obj === "object") {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        // Check for top-level custom key remapping
        if (customKeyMap[key]) {
          acc[customKeyMap[key]] = convertKeysToPascalCase(value);
        }
        // Handle address object specially
        else if (key.toLowerCase().includes("address") && typeof value === "object" && value !== null) {
          const flatAddress = convertKeysToPascalCase(value);
          Object.entries(flatAddress).forEach(([addrKey, addrValue]) => {
           
            const mappedKey = addressKeyMap[addrKey.toLowerCase()] || toPascalCase(addrKey);
            acc[mappedKey] = addrValue;
          });
        }
        // Standard PascalCase key
        else {
          const pascalKey = toPascalCase(key);
          acc[pascalKey] = convertKeysToPascalCase(value);
        }

        return acc;
      }, {} as Record<string, any>);
    }

    return obj;
};

export const htmlToPlainText = (html: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
};


export const isLexicalEditorEmpty = (editorState: any) => {
  let empty = false;

  editorState.read(() => {
    const root = $getRoot();
    const children = root.getChildren();
    // Check if there's exactly one paragraph node and it's empty
    if(children?.length === 0) empty = true;
    else if (
      children.length === 1 &&
      $isParagraphNode(children[0]) &&
      children[0].getTextContent().trim() === "" &&
      children[0].getChildren().length === 0
    ) {
      empty = true;
    }
  });

  return empty;
};